import { FormHelperText, Stack, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ButtonGroupFilter, { IListButtonItem } from 'src/components/common/ButtonGroupFilter';
import DatePickerWithDefaultIcon from 'src/components/DatePicker';
import { useQueryRuntimeConfig } from 'src/services/queries/useQueryRuntimeConfig';
import { FORMAT_DATE_TIME } from 'src/utils/constants/config';
import {
  KeyFilterChartCTDRelationshipView,
  KeyFilterChartTradeDataUnitBy,
  KeyFilterChartTradeDataViewBy,
} from 'src/utils/constants/enum';
import { getTradeDataEndMonth } from 'src/utils/helpers/dayjs';

interface IProps {
  title?: string | React.ReactNode;
  selectedUnitBy?: IListButtonItem['key'];
  selectedRangeTime: { fromTime: string; toTime: string };
  selectedViewBy?: IListButtonItem['key'];
  selectedRelationship?: IListButtonItem['key'];
  onSelectRelationship?: (key: IListButtonItem['key']) => void;
  onSelectUnitBy?: (key: IListButtonItem['key']) => void;
  onSelectTimeRange: (arr: { fromTime: string; toTime: string }) => void;
  onSelectViewBy?: (key: IListButtonItem['key']) => void;
  // isError?: boolean
  // errorMessage?: string
}

const FilterChart = ({
  title,
  onSelectTimeRange,
  selectedRangeTime = { fromTime: '', toTime: '' },
  onSelectUnitBy,
  selectedUnitBy,
  onSelectViewBy,
  selectedViewBy,
  onSelectRelationship,
  selectedRelationship,
  // isError,
  // errorMessage
}: IProps) => {
  const [fromTime, setFromTime] = useState(selectedRangeTime.fromTime);
  const [toTime, setToTime] = useState(selectedRangeTime.toTime);
  const { t } = useTranslation();
  const {
    data: { data: runtimeConfig },
  } = useQueryRuntimeConfig();
  // const isError = dayjs(toTime).diff(dayjs(fromTime), 'month') > TRADE_DATA_MAXIMUM_SUPPORTED_DATE_RANGE_MONTHS;
  const isError = false;
  const errorMessage = t('trade_data.common.field.error.date_range.exceed');

  const renderTitle = () => {
    if (!title) return null;
    if (typeof title === 'string') return <Typography variant="h5">{title}</Typography>;
    else {
      return title;
    }
  };

  useEffect(() => {
    if (isError) return;
    if (fromTime && toTime) {
      onSelectTimeRange({
        fromTime: fromTime,
        toTime: toTime,
      });
    }
  }, [fromTime, toTime]);

  return (
    <Stack flexWrap={'wrap'} gap={3} direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
      {renderTitle()}
      <Stack direction={'column'}>
        <Stack gap={3} direction={'row'} alignItems={'center'}>
          <Stack direction={'column'}>
            <Stack maxWidth={300} direction={'row'} alignItems={'center'} gap={1}>
              <DatePickerWithDefaultIcon
                value={dayjs(fromTime)}
                disableFuture={true}
                // disabled={true}
                minDate={dayjs(runtimeConfig?.MIN_TRADE_DATA_DATE, 'YYYY-MM-DD')}
                maxDate={dayjs(runtimeConfig?.MAX_TRADE_DATA_DATE, 'YYYY-MM-DD')}
                onChange={(value) => setFromTime(dayjs(value).startOf('month').format(FORMAT_DATE_TIME.yyyy_mm_dd))}
                label={t('trade_data.search_form.input.from')}
                format={FORMAT_DATE_TIME.MONTH_YEAR}
                slotProps={{
                  textField: {
                    size: 'small',
                    error: isError,
                    required: true,
                  },
                }}
                views={['month', 'year']}
              />
              -
              <DatePickerWithDefaultIcon
                value={dayjs(toTime)}
                disableFuture={true}
                // disabled={true}
                format={FORMAT_DATE_TIME.MONTH_YEAR}
                minDate={dayjs(runtimeConfig?.MIN_TRADE_DATA_DATE, 'YYYY-MM-DD')}
                maxDate={dayjs(runtimeConfig?.MAX_TRADE_DATA_DATE, 'YYYY-MM-DD')}
                onChange={(value) => setToTime(getTradeDataEndMonth(dayjs(value)).format(FORMAT_DATE_TIME.yyyy_mm_dd))}
                label={t('trade_data.search_form.input.trade_to')}
                slotProps={{
                  textField: {
                    size: 'small',
                    error: isError,
                    required: true,
                  },
                }}
                views={['month', 'year']}
              />
            </Stack>
          </Stack>
          {onSelectUnitBy && selectedUnitBy && (
            <ButtonGroupFilter
              size="small"
              label="Unit By"
              buttons={[
                {
                  label: 'No. of B/L',
                  key: KeyFilterChartTradeDataUnitBy.bol,
                },
                {
                  label: 'Weight',
                  key: KeyFilterChartTradeDataUnitBy.weight,
                },
                {
                  label: 'Quantity',
                  key: KeyFilterChartTradeDataUnitBy.quantity,
                },
                {
                  label: 'USD',
                  key: KeyFilterChartTradeDataUnitBy.value_usd,
                },
              ]}
              active={selectedUnitBy}
              onClick={onSelectUnitBy}
            />
          )}
          {onSelectViewBy && selectedViewBy && (
            <ButtonGroupFilter
              size="small"
              label="View By"
              buttons={[
                {
                  label: 'Monthly',
                  key: KeyFilterChartTradeDataViewBy.monthly,
                },
                {
                  label: 'Annual',
                  key: KeyFilterChartTradeDataViewBy.annual,
                },
              ]}
              active={selectedViewBy}
              onClick={onSelectViewBy}
            />
          )}
          {selectedRelationship && onSelectRelationship && (
            <ButtonGroupFilter
              size="small"
              label="Relationship with"
              buttons={[
                {
                  label: 'SUPPLIERS',
                  key: KeyFilterChartCTDRelationshipView.supplier,
                },
                {
                  label: 'BUYERS',
                  key: KeyFilterChartCTDRelationshipView.buyer,
                },
              ]}
              active={selectedRelationship}
              onClick={onSelectRelationship}
            />
          )}
        </Stack>
        {isError && (
          <FormHelperText error id="standard-weight-helper-text-email-login">
            {errorMessage}
          </FormHelperText>
        )}
      </Stack>
    </Stack>
  );
};

export default FilterChart;
